// Shape deliberately mirrors the delete confirm: same modal frame, same weight on the sentence that
// cannot be taken back. What differs is that this one has to explain a CONSEQUENCE rather than just
// a loss — the combined field's maps are re-derived, and values move in the smaller sections.
import { useEffect, useState } from "react";
import { AlertTriangle, Loader2, X } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "../../api";
import { ApiError } from "../../api/client";
import { qk } from "../../hooks/queries";
import { useFieldToolsStore } from "../../stores/fieldToolsStore";
import { useMergeWatch } from "../../stores/mergeWatchStore";
import { combineBody, combineConfirmLabel, combineTitle } from "./combineCopy";
import {
  isMergeable,
  mergeRefusals,
  mergeRefusalsFromError,
  type MergeRefusal,
} from "./mergeGate";
import type { DashboardField } from "../../types/contract";
import { Modal } from "../../ui/Modal";

export function CombineFieldsModal({
  fields,
  onClose,
}: {
  fields: DashboardField[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [checking, setChecking] = useState(true);
  const [refusals, setRefusals] = useState<MergeRefusal[] | null>(null);
  const [checkErr, setCheckErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // The merged field is a NEW field and nothing can name it — the merge record holds the sources
  // only, and no rule picks fairly between "Home 80" and "Home 40".
  const [name, setName] = useState("");

  // largest section first: it is the one whose values come out unchanged (compute anchors the union
  // lattice to it), so the copy names it — and the caller must not have to know that rule.
  const ordered = [...fields].sort((a, b) => (b.acres ?? 0) - (a.acres ?? 0));
  const names = ordered.map((f) => f.field_name);
  const anchor = ordered[0];
  useEffect(() => setName(anchor?.field_name ?? ""), [anchor?.field_name]);

  useEffect(() => {
    let alive = true;
    setChecking(true);
    setCheckErr(null);
    void api
      .mergeFieldsPreflight(fields.map((f) => f.field_id))
      .then((res) => {
        if (!alive) return;
        setRefusals(mergeRefusals(res?.reasons));
        // read the REASONS, not the served boolean, so the button and the sentence under it can
        // never disagree; and a malformed answer lands as "not mergeable", never as a yes.
        if (!isMergeable(res?.reasons) && !(res?.reasons ?? []).length) {
          setCheckErr("The server didn’t say whether these can be combined.");
        }
      })
      .catch((e) => {
        if (!alive) return;
        // A failed check is NOT a yes. Say why, and offer nothing.
        setCheckErr(
          e instanceof ApiError
            ? e.message
            : "Couldn’t check whether these can be combined.",
        );
      })
      .finally(() => alive && setChecking(false));
    return () => {
      alive = false;
    };
  }, [fields]);

  const nameOk = name.trim().length > 0;
  const canCombine = refusals != null && refusals.length === 0 && !checkErr;

  const doCombine = async () => {
    setBusy(true);
    setErr(null);
    try {
      const res = await api.mergeFields({
        field_ids: ordered.map((f) => f.field_id),
        name: name.trim(),
        folder_id: anchor!.folder_id ?? null,
      });
      // HAND OFF AND GET OUT OF THE WAY — "It should run in the background though, instead of
      // forcing the user to stay on the confirmation screen until it finishes").
      if (res?.merge_id) {
        useMergeWatch.getState().start({
          merge_id: res.merge_id,
          name: name.trim(),
          source_field_ids: ordered.map((f) => f.field_id),
          source_names: names,
        });
      }
      // The selection is cleared either way: these fields are spoken for, and leaving them ticked
      // would invite a second merge on the same ground.
      useFieldToolsStore.getState().clearSelection();
      void qc.invalidateQueries({ queryKey: qk.fields });
      void qc.invalidateQueries({ queryKey: ["field-jobs"] });
      onClose();
    } catch (e) {
      // A 409 is the GATE'S ANSWER, re-run at authorise time — not a fault.
      const refused = mergeRefusalsFromError(e);
      if (refused) setRefusals(refused);
      // The sources are released automatically, so the honest line is the OUTCOME: nothing was
      // combined.
      else if (e instanceof ApiError && e.code === "ERR_DEPENDENCY_UNAVAILABLE")
        setErr(
          "The merge couldn’t be started just now. Nothing was combined — your fields are unchanged. Try again in a few minutes.",
        );
      // Otherwise: stay open with the error. A dialog that closed on failure would read as success on
      // an action the user cannot verify by looking — and cannot undo if it did happen.
      else
        setErr(
          e instanceof ApiError
            ? e.message
            : e instanceof Error
              ? e.message
              : "Couldn’t combine these fields.",
        );
      setBusy(false);
    }
  };

  return (
    <Modal>
      <div
        className="fixed inset-0 z-dialog flex items-center justify-center bg-black/40 p-4"
        onClick={() => !busy && onClose()}
      >
        <div
          className="flex w-full max-w-md flex-col rounded-lg bg-surface text-fg shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between border-b border-line px-3 py-2">
            <h2 className="text-sm font-semibold">
              {combineTitle(fields.length)}
            </h2>
            <button
              onClick={onClose}
              disabled={busy}
              className="rounded p-1 hover:bg-hover disabled:opacity-50"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>

          <div className="flex max-h-[70vh] flex-col gap-3 overflow-auto p-4 text-[12px]">
            {checking && (
              <p className="flex items-center gap-1.5 text-muted">
                <Loader2 size={12} className="animate-spin" /> Checking whether
                these can be combined…
              </p>
            )}

            {checkErr && <p className="text-amber-500">{checkErr}</p>}

            {/*
 REFUSALS: the whole reason the pre-flight exists. Each says what is true and, where the
              user can act, what to do — `kind` decides the tone rather than the wording.
*/}
            {refusals != null && refusals.length > 0 && (
              <div className="flex flex-col gap-2">
                {refusals.map((r) => (
                  <div
                    key={r.code + r.text}
                    className="flex items-start gap-2 rounded border border-line bg-canvas/40 p-2"
                  >
                    <AlertTriangle
                      size={13}
                      className={`mt-0.5 shrink-0 ${r.kind === "fix" ? "text-amber-500" : "text-muted"}`}
                    />
                    <span className="flex-1">
                      {r.text}
                      {/* the server's own words, kept as small print rather than spliced into the sentence */}
                      {r.detail && (
                        <span className="mt-0.5 block text-[10px] text-faint">
                          {r.detail}
                        </span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {canCombine && (
              <label className="flex flex-col gap-1">
                <span className="text-[11px] font-medium text-muted">
                  Name for the combined field
                </span>
                <input
                  className="rounded border border-line bg-canvas px-2 py-1.5 text-[12px] text-fg outline-none focus:border-accent"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  aria-label="Name for the combined field"
                  disabled={busy}
                />
                {!nameOk && (
                  <span className="text-[10px] text-amber-500">
                    The combined field needs a name.
                  </span>
                )}
              </label>
            )}

            {canCombine &&
              combineBody({ fieldNames: names, anchorName: names[0]! }).map(
                (p, i, all) => (
                  <p
                    key={p}
                    className={
                      i === all.length - 1
                        ? "font-medium text-red-400"
                        : "text-muted"
                    }
                  >
                    {p}
                  </p>
                ),
              )}

            {err && <p className="text-red-400">{err}</p>}
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-line px-3 py-2">
            <button
              onClick={onClose}
              disabled={busy}
              className="rounded border border-line px-3 py-1.5 text-[12px] text-fg hover:bg-hover disabled:opacity-50"
            >
              {canCombine ? "Cancel" : "Close"}
            </button>
            {canCombine && (
              <button
                onClick={() => void doCombine()}
                disabled={busy || !nameOk}
                className="flex items-center gap-1.5 rounded border border-red-500 bg-red-500/90 px-3 py-1.5 text-[12px] font-medium text-white hover:bg-red-500 disabled:opacity-50"
              >
                {busy && <Loader2 size={12} className="animate-spin" />}{" "}
                {combineConfirmLabel}
              </button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
